import axios from 'axios';
import { API_BASE_URL } from './reviewService';

type Primitive = string | number | boolean;

type FilterValue = Primitive | Primitive[] | Record<string, Primitive>;

export interface RAGFilterPayload {
  sentiment?: string[];
  rating?: number[] | { min?: number; max?: number };
  source?: string[];
  platform?: string[];
  priority?: string[];
  media_type?: string[];
  emotional_intensity?: string[];
  has_transformation?: boolean;
  only_with_insights?: boolean;
  date_range?: string;
  angles?: string[];
  topics?: string[];
  professions?: string[];
}

export interface RAGSearchRequest {
  query: string;
  filters?: RAGFilterPayload;
  top_k?: number;
  include_raw_results?: boolean;
}

export interface RAGSearchSummary {
  average_rating?: number | null;
  sentiments?: Record<string, number>;
  sources?: Record<string, number>;
}

export interface RAGSearchMetadata {
  brand: string;
  timestamp: string;
  results_count: number;
  top_k_applied?: number;
  filters_applied?: Record<string, FilterValue>;
  summary?: RAGSearchSummary;
}

export interface RAGHighlight {
  text: string;
  angle?: string;
  category?: string;
  quality?: number;
}

export interface RAGSearchResult {
  id: string;
  score: number;
  distance: number;
  rating?: number | null;
  sentiment?: string;
  source?: string;
  platform?: string;
  createdAt?: string;
  reviewSnippet: string;
  reviewBody: string;
  reviewerName?: string;
  highlights: RAGHighlight[];
  metadata?: Record<string, unknown>;
}

export interface RAGSearchResponse {
  query: string;
  response: string;
  metadata: RAGSearchMetadata;
  results?: RAGSearchResult[];
}

const RAG_ENDPOINT = `${API_BASE_URL}/v1/rag/query`;

function normaliseResult(raw: any): RAGSearchResult {
  return {
    id: raw?.id,
    score: raw?.score ?? 0,
    distance: raw?.distance ?? 0,
    rating: raw?.rating ?? null,
    sentiment: raw?.sentiment ?? '',
    source: raw?.source ?? '',
    platform: raw?.platform ?? '',
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    reviewSnippet: raw?.reviewSnippet ?? raw?.review_snippet ?? '',
    reviewBody: raw?.reviewBody ?? raw?.review_body ?? '',
    reviewerName: raw?.reviewerName ?? raw?.reviewer_name ?? '',
    highlights: Array.isArray(raw?.highlights) ? raw.highlights : [],
    metadata: raw?.metadata ?? {},
  };
}

export const ragService = {
  async search(params: RAGSearchRequest): Promise<RAGSearchResponse> {
    const payload = {
      query: params.query,
      filters: params.filters,
      top_k: params.top_k,
      include_raw_results: params.include_raw_results ?? true,
    };

    const response = await axios.post(RAG_ENDPOINT, payload);
    const data = response.data ?? {};

    const results = Array.isArray(data.results)
      ? data.results.map(normaliseResult)
      : undefined;

    return {
      query: data.query ?? params.query,
      response: data.response ?? '',
      metadata: data.metadata ?? {
        brand: '',
        timestamp: new Date().toISOString(),
        results_count: results?.length ?? 0,
      },
      results,
    };
  },
};
